import { createContext, useContext, useEffect, useState } from "react";
import { STRINGS, LABELS_FR, setCurrentLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
    const { user } = useAuth();
    const [lang, setLangState] = useState(() => localStorage.getItem("apptrack_lang") || "en");

    // Apply the signed-in user's saved preference once known
    useEffect(() => {
        if (user?.lang && user.lang !== lang) {
            setLangState(user.lang);
            localStorage.setItem("apptrack_lang", user.lang);
        }
        // eslint-disable-next-line
    }, [user?.lang]);

    const setLang = (l) => {
        setLangState(l);
        setCurrentLang(l);
        localStorage.setItem("apptrack_lang", l);
        if (user) api.patch("/auth/me/preferences", { lang: l }).catch(() => {});
    };

    useEffect(() => {
        setCurrentLang(lang);
        document.documentElement.lang = lang;
    }, [lang]);

    const t = (key) => STRINGS[lang]?.[key] || STRINGS.en[key] || key;
    const tr = (s) => (lang === "fr" ? (LABELS_FR[s] || s) : s);
    return (
        <LanguageContext.Provider value={{ lang, setLang, t, tr }}>
            {children}
        </LanguageContext.Provider>
    );
}

export const useLanguage = () =>
    useContext(LanguageContext) || { lang: "en", setLang: () => {}, t: (k) => STRINGS.en[k] || k, tr: (s) => s };

export function LangSwitcher({ dark = false, testid = "lang-switcher" }) {
    const { lang, setLang } = useLanguage();
    return (
        <div data-testid={testid} title="Language / Langue"
            className={`flex items-center gap-0.5 rounded-full p-0.5 border text-[11px] font-bold ${dark ? "border-white/20 bg-white/10" : "border-slate-200 bg-slate-100/70"}`}>
            {["en", "fr"].map((l) => (
                <button key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)}
                    className={`px-2.5 py-1 rounded-full uppercase tracking-wide transition-colors ${lang === l ? (dark ? "bg-[#00B4D8] text-white" : "bg-[#0A2540] text-white") : dark ? "text-white/50 hover:text-white" : "text-slate-500 hover:text-[#0092B0]"}`}>
                    {l}
                </button>
            ))}
        </div>
    );
}
